'use client';

import { format, startOfDay } from 'date-fns';
import { CalendarIcon } from 'lucide-react';

import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { cn } from '@/utils/styles';

// ─── Constants ────────────────────────────────────────────────────────────────

/** All 288 time slots from 12:00am to 11:55pm in 5-minute steps. */
const TIME_SLOTS: readonly string[] = (() => {
	const slots: string[] = [];
	for (let h = 0; h < 24; h++) {
		for (let m = 0; m < 60; m += 5) {
			const period = h < 12 ? 'am' : 'pm';
			const displayHour = h % 12 === 0 ? 12 : h % 12;
			const displayMinute = String(m).padStart(2, '0');
			slots.push(`${displayHour}:${displayMinute}${period}`);
		}
	}
	return slots;
})();

// ─── Helpers ──────────────────────────────────────────────────────────────────

function extractTimeSlot(date: Date): string {
	const h24 = date.getHours();
	const period = h24 < 12 ? 'am' : 'pm';
	const displayHour = h24 % 12 === 0 ? 12 : h24 % 12;
	const displayMinute = String(Math.floor(date.getMinutes() / 5) * 5).padStart(2, '0');
	return `${displayHour}:${displayMinute}${period}`;
}

function applyTimeSlot(date: Date, slot: string): Date {
	const result = new Date(date);
	const match = slot.match(/^(\d+):(\d+)(am|pm)$/);
	if (!match) return result;
	let h = parseInt(match[1], 10);
	const m = parseInt(match[2], 10);
	if (match[3] === 'am' && h === 12) h = 0;
	if (match[3] === 'pm' && h !== 12) h += 12;
	result.setHours(h, m, 0, 0);
	return result;
}

// ─── TimePickerColumn ─────────────────────────────────────────────────────────

type TimePickerColumnProps = {
	items: readonly string[];
	selected: string;
	onSelect: (value: string) => void;
};

function TimePickerColumn({ items, selected, onSelect }: TimePickerColumnProps) {
	const containerRef = React.useRef<HTMLDivElement>(null);
	const selectedRef = React.useRef<HTMLButtonElement>(null);

	React.useEffect(() => {
		const container = containerRef.current;
		const el = selectedRef.current;
		if (!container || !el) return;
		container.scrollTo({
			top: el.offsetTop - container.clientHeight / 2 + el.clientHeight / 2,
			behavior: 'instant',
		});
	}, []);

	return (
		<div
			ref={containerRef}
			className="flex max-h-[280px] w-[90px] flex-col overflow-y-auto [&::-webkit-scrollbar]:hidden"
			style={{ scrollbarWidth: 'none' }}
		>
			{items.map((item) => (
				<button
					key={item}
					ref={item === selected ? selectedRef : undefined}
					onClick={() => onSelect(item)}
					className={cn(
						'my-0.5 w-full rounded-md px-2 py-1.5 text-center text-sm font-medium transition-colors',
						item === selected
							? 'bg-primary text-white'
							: 'text-slate-500 hover:bg-slate-100 hover:text-primary'
					)}
				>
					{item}
				</button>
			))}
		</div>
	);
}

// ─── DatePicker ───────────────────────────────────────────────────────────────

type DatePickerProps = {
	disabled?: boolean;
	selectedDate?: Date;
	onSelectDate?: (date: Date | undefined) => void;
	className?: string;
	disallowPastDates?: boolean;
	modal?: boolean;
	/**
	 * When true, a time picker is shown side-by-side with the calendar.
	 * Selecting a date keeps the popover open. Every change emits immediately.
	 */
	showTime?: boolean;
	/** When true, the calendar icon is not rendered inside the trigger (e.g. when using an external icon as label). */
	hideTriggerIcon?: boolean;
	/**
	 * date-fns format string used for the trigger label when a date is selected and `showTime` is false.
	 * Defaults to `'PPP'` (e.g. "March 12th, 2025"). Use `'EEEE, MMMM d'` for "Tuesday, March 12".
	 */
	dateFormat?: string;
	/** Placeholder text shown in the trigger when no date is selected. Defaults to "Pick a date". */
	placeholder?: string;
};

function DatePicker({
	disabled,
	selectedDate,
	onSelectDate,
	className,
	disallowPastDates,
	modal,
	showTime = false,
	hideTriggerIcon = false,
	dateFormat = 'PPP',
	placeholder,
}: DatePickerProps) {
	const [open, setOpen] = React.useState(false);
	const [date, setDate] = React.useState<Date | undefined>(selectedDate);
	const [timeSlot, setTimeSlot] = React.useState(() => extractTimeSlot(selectedDate ?? new Date()));

	// Sync local state with selectedDate whenever the popover opens
	const handleOpenChange = React.useCallback(
		(isOpen: boolean) => {
			if (isOpen) {
				setDate(selectedDate);
				setTimeSlot(extractTimeSlot(selectedDate ?? new Date()));
			}
			setOpen(isOpen);
		},
		[selectedDate]
	);

	const handleSelectDate = React.useCallback(
		(newDate: Date | undefined) => {
			if (!showTime) {
				const next = newDate?.toDateString() === selectedDate?.toDateString() ? undefined : newDate;
				setDate(next);
				onSelectDate?.(next);
				setOpen(false);
				return;
			}
			setDate(newDate);
			onSelectDate?.(newDate ? applyTimeSlot(newDate, timeSlot) : undefined);
		},
		[showTime, selectedDate, onSelectDate, timeSlot]
	);

	const handleTimeSlot = React.useCallback(
		(slot: string) => {
			setTimeSlot(slot);
			if (date) onSelectDate?.(applyTimeSlot(date, slot));
		},
		[date, onSelectDate]
	);

	const displayLabel = React.useMemo(() => {
		if (!date) return placeholder ?? (showTime ? 'Pick a date & time' : 'Pick a date');
		if (showTime) return format(applyTimeSlot(date, timeSlot), `${dateFormat}, h:mm a`);
		return format(date, dateFormat);
	}, [date, showTime, timeSlot, dateFormat, placeholder]);

	// Keep local date in sync when selectedDate changes externally (non-showTime)
	React.useEffect(() => {
		if (!showTime) setDate(selectedDate);
	}, [selectedDate, showTime]);

	return (
		<Popover
			modal={modal}
			open={open}
			onOpenChange={handleOpenChange}
		>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					className={cn(
						'h-[44px] w-[240px] justify-start text-left font-normal text-slate-700 disabled:cursor-not-allowed disabled:opacity-100',
						!date && 'text-muted-foreground',
						!hideTriggerIcon && 'gap-2',
						className
					)}
					disabled={disabled}
				>
					{!hideTriggerIcon && <CalendarIcon className="h-4 w-4 shrink-0" />}
					<span className="truncate">{displayLabel}</span>
				</Button>
			</PopoverTrigger>

			<PopoverContent
				align="start"
				className="w-auto p-0"
			>
				<div className="flex divide-x divide-border">
					<Calendar
						mode="single"
						captionLayout="dropdown-buttons"
						selected={date}
						defaultMonth={date}
						onSelect={handleSelectDate}
						fromYear={1960}
						toYear={2030}
						disabled={(d) => (disallowPastDates ? d < startOfDay(new Date()) : false)}
					/>

					{showTime && (
						<div className="p-2">
							<TimePickerColumn
								items={TIME_SLOTS}
								selected={timeSlot}
								onSelect={handleTimeSlot}
							/>
						</div>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}

export default DatePicker;
